import React, { useRef, useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { storage } from '../firebase'; // adjust the path as needed
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from './AuthProvider';

export const ImagesField = ({ label, images, onReorderImages, venueId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const { userInfo } = useAuth();
  const isAdmin = userInfo?.role === "admin";

  // Admin-only helper: copy the Firebase Storage URL so it can be pasted into
  // a marketing template. Show a transient "Copiado" badge.
  const copyImageUrl = (url, index) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex((i) => (i === index ? null : i)), 1500);
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const newImages = Array.from(images);
    const [movedImage] = newImages.splice(result.source.index, 1);
    newImages.splice(result.destination.index, 0, movedImage);
    onReorderImages(newImages);
  };
  

  const handleDeleteImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onReorderImages(newImages);
  };

  const uploadImage = useCallback(async (file) => {
    try {
      const imagePath = `venues/${venueId}/image-${Date.now()}-${file.name}`;
      const storageRef = ref(storage, imagePath);
      // 1y immutable cache on the file's HTTP response — lets Gmail's image
      // proxy, browser caches, and any CDN in front (our Vercel proxy) keep
      // the bytes around between fetches. Safe because filenames include a
      // timestamp, so reuploads always change the URL.
      await uploadBytes(storageRef, file, {
        cacheControl: "public, max-age=31536000, immutable",
      });
      const downloadUrl = await getDownloadURL(storageRef);
      return downloadUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  }, [venueId]);

  const handleFilesUpload = useCallback(async (files) => {
    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploadPromises = Array.from(files).map(uploadImage);
      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter(url => url !== null);
      
      if (validUrls.length) {
        onReorderImages([...images, ...validUrls]);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [uploadImage, images, onReorderImages]);

  const handleFileChange = useCallback((event) => {
    handleFilesUpload(event.target.files);
  }, [handleFilesUpload]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    handleFilesUpload(droppedFiles);
  }, [handleFilesUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.add('bg-gray-100');
    }
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropZoneRef.current) {
      dropZoneRef.current.classList.remove('bg-gray-100');
    }
  }, []);

  return (
    <fieldset className="p-4 border rounded mb-4">
      <legend className="font-semibold mb-2">{label}</legend>

      {/* Drag and Drop Zone */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className="mb-4 p-6 border-2 border-dashed border-gray-300 rounded-lg text-center transition-colors duration-200 ease-in-out"
      >
        <p className="text-gray-600">
          Arrastra y suelta imágenes aquí o
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="text-blue-500 hover:text-blue-700 font-medium mx-1"
          >
            selecciona archivos
          </button>
        </p>
        {uploading && (
          <div className="mt-2">
            <div className="h-2 w-full bg-gray-200 rounded">
              <div
                className="h-full bg-blue-500 rounded transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="imagesDroppable" direction="horizontal">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className="flex flex-wrap gap-4 p-2 border border-gray-300 rounded-md bg-gray-50 min-h-[120px]"
            >
              {/* key/draggableId compuestos con el índice: una URL duplicada
                  en el array (pasa en data seedeada) rompía la reconciliación
                  de React — borrar una imagen hacía aparecer otras. */}
              {images.map((url, index) => (
                <Draggable key={`${index}::${url}`} draggableId={`${index}::${url}`} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      className="relative group"
                    >
                      <div className="relative p-1 border border-gray-400 rounded-md bg-white shadow group-hover:shadow-lg transition-shadow duration-200">
                        <img
                          src={url}
                          alt={`preview-${index}`}
                          className="w-24 h-24 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => handleDeleteImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                        >
                          ×
                        </button>
                        {isAdmin && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyImageUrl(url, index);
                            }}
                            title="Copiar URL para templates de marketing"
                            className="absolute bottom-1 left-1 right-1 text-[10px] py-0.5 rounded bg-zinc-900/80 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-zinc-900"
                          >
                            {copiedIndex === index ? "✓ Copiado" : "Copiar URL"}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <input
        type="file"
        accept="image/*"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
    </fieldset>
  );
};
